import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  OnInit,
  Inject,
  HostListener,
  OnDestroy,
  ViewChildren,
  DoCheck,
  QueryList,
  ElementRef,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PvButtonModule } from '@pv-frontend/pv-shared-components/button';
import { OverrideStyleConfig } from '@pv-frontend/pv-shared-components/common/types';
import { PvDrawerModule } from '@pv-frontend/pv-shared-components/drawer';
import { SvgIconModule } from '@pv-frontend/pv-shared-components/svg-icon';
import { DeviceService } from '@pv-frontend/pv-shared-services/device-service';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';
import { TooltipModule } from 'primeng/tooltip';
import {
  OtpResponse,
  VerifyOtpResponse,
} from 'projects/visa-platform/src/app/shared/services/auth.interface';
import { AuthService } from 'projects/visa-platform/src/app/shared/services/auth.service';
import {
  ERROR_MESSAGES,
  ERROR_CODES,
  AUTH_SCREENS,
} from 'projects/visa-platform/src/app/shared/services/constants';
import { RazorpayAnalyticsService } from 'projects/visa-platform/src/app/shared/services/razorpay-analytics.service';
import { environment } from 'projects/visa-platform/src/environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StorageService } from '../../services/storage.service';
import { CommonAnchorComponent } from '../common-anchor/common-anchor.component';

import { User, Card, EnvironmentType, CurrentUser } from './signup.interface';
@Component({
  selector: 'visa-platform-signup',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PvButtonModule,
    PvDrawerModule,
    SvgIconModule,
    TooltipModule,
    RecaptchaV3Module,
    CommonAnchorComponent,
  ],
  templateUrl: 'signup.component.html',
  styleUrls: ['signup.component.scss'],
})
export class SignupComponent implements OnInit, OnDestroy, DoCheck {
  public env = environment;
  public showModal: boolean = true;

  @ViewChildren('cardFormRow') public cardFormRows!: QueryList<ElementRef>;
  @ViewChildren('emailInput') public emailInputRef!: QueryList<ElementRef>;
  @ViewChildren('mobileInput') public mobileInputRef!: QueryList<ElementRef>;
  public pvDrawerConfig: OverrideStyleConfig = {
    borderRadius: this.deviceService.isCurrentDeviceMobile()
      ? '15px 15px 0 0'
      : '15px',
    overflow: 'auto',
    minHeight: '75vh',
    minWidth: this.deviceService.isCurrentDeviceMobile() ? '100%' : '',
    maxWidth: !this.deviceService.isCurrentDeviceMobile() ? '400px' : '',
  };
  public showAuthSection: boolean;
  public changeNumberFLag = false;
  public isLoggedIn = false;
  public environment_type: EnvironmentType;

  public authForm: FormGroup;
  public otpForm: FormGroup;
  public cardDetailsForm: FormGroup;
  public otpFormInput = [
    'input1',
    'input2',
    'input3',
    'input4',
    'input5',
    'input6',
  ];
  public cardFormInput = ['inpOne', 'inpTwo', 'inpThree', 'inpFour'];
  public cardExpiryFormInput = ['cardMM', 'cardYY'];
  public submitted = false;
  private destroyed$: Subject<boolean> = new Subject();
  public submitBtnDisable = false;
  public cardInputEntered = false;
  public customErrorMessage: string = '';
  public submitBtnName: string | null = '';
  public submitBtnTitle: string | null = '';
  public focusedElement: string | null = null;
  public activeScreen?: string =
    new URL(window.location.href).searchParams.get('step') === 'verification'
      ? 'verification'
      : (new URL(window.location.href).searchParams.get('step') ?? 'mobile');
  public reverifyFlag: boolean =
    Boolean(
      new URL(window.location.href).searchParams.get('reverify') === 'true'
    ) ?? false;
  public addNewCardFlag: boolean =
    Boolean(
      new URL(window.location.href).searchParams.get('add-new-card') === 'true'
    ) ?? false;
  public previousScreen?: string = 'mobile';
  public screens = [
    'landing', // get start
    'mobile', // unlock my vouchers
    'otp', // verify otp
    'card', // authenticate my card
    'bankVerification', // waiting for verification
    'bankAuthenticated', // authenticated by bank
    'bankAuthenticationFailed', // authentication fail by bank
  ];
  public otpTimer: string | null = null;
  @ViewChildren('otpFormRow') public otpFormRows!: QueryList<ElementRef>;
  public addNewCard = false;
  public isCardsDropdownOpen = false;
  public currentUser: CurrentUser = {} as CurrentUser;
  public userCards: Card[] = [];
  public activeUserCard?: Card;
  public showLoader = false;
  public isSignUp = true;
  public isOtpResent = false;
  public emailLength: {
    min: number;
    max: number;
  } = { min: 6, max: 40 };
  public mobileNumberLength: {
    min: number;
    max: number;
  } = { min: 8, max: 12 };
  public readonly EMAIL_PATTERN: RegExp =
    /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/;

  // Authentication mode configuration
  public authMode = this.env.authMode;
  public countryCode = this.env.countryCode;

  public userId = '';
  public cardNumber = '';
  public emailId = '';
  public mobileNumber = '';
  public isCaptchaVerified = false;
  public captchaToken = '';

  public isMobile = this.deviceService.isCurrentDeviceMobile();
  public showCheckboxes: boolean = false;

  @HostListener('unloaded')
  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public constructor(
    private router: Router,
    public authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
    private fb: UntypedFormBuilder,
    public deviceService: DeviceService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private route: ActivatedRoute,
    private analyticsService: RazorpayAnalyticsService,
    private storageService: StorageService
  ) {
    this.showAuthSection = true;
    this.environment_type = this.document.location.hostname.includes('sandbox')
      ? 'sandbox'
      : 'prod';

    const authFieldConfig: {
      emailId?: [null, ValidatorFn[]];
      mobileNumber?: [null, ValidatorFn[]];
    } = {};

    if (this.authMode === 'email') {
      authFieldConfig['emailId'] = [
        null,
        [Validators.required, this.checkMinLength(5)],
      ];
    } else {
      authFieldConfig['mobileNumber'] = [
        null,
        [
          Validators.required,
          this.checkMinLength(this.mobileNumberLength.min),
          this.checkMaxLength(this.mobileNumberLength.max),
        ],
      ];
    }

    this.authForm = this.fb.group({
      inpOne: [null, [Validators.required, this.checkMinLength(4)]],
      inpTwo: [null, [Validators.required, this.checkMinLength(4)]],
      inpThree: [null, [Validators.required, this.checkMinLength(4)]],
      inpFour: [null, [Validators.required, this.checkMinLength(4)]],
      ...authFieldConfig,
      consent1: [null, [Validators.required]],
      consent2: [null, [Validators.required]],
    });
    this.otpForm = this.toFormGroup(this.otpFormInput);
    this.cardDetailsForm = this.fb.group({
      expiryMM: [null, [Validators.required, this.checkValidMMExpiry()]],
      expiryYY: [null, [Validators.required, this.checkValidYYExpiry()]],
    });
    this.handleCheckboxVisibility();
  }

  public ngOnInit(): void {
    this.authForm?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.submitBtnDisable = !this.authForm?.valid;
        this.submitted = false;
      });

    this.cardDetailsForm?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (
          (this.cardNumber.trim() == '' || this.cardNumber.length == 0) &&
          (value?.expiryMM?.trim() == '' || value?.expiryMM == null) &&
          (value?.expiryYY?.trim() == '' || value?.expiryYY == null)
        ) {
          this.cardInputEntered = false;
        } else {
          this.cardInputEntered = true;
        }
        this.submitted = false;
      });

    this.otpForm?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.otpForm?.valid) {
          this.submitBtnDisable = false;
        }
      });
    const currentURL = new URL(window.location.href);
    const cardIdInRoute: string | null = currentURL.searchParams.get('cardId');
    if (
      (JSON.parse(localStorage.getItem('currentUser') || '{}') as User)
        ?.token &&
      cardIdInRoute
    ) {
      this.handleUserLogin(currentURL, cardIdInRoute);
    }
    if (this.reverifyFlag || this.addNewCardFlag) {
      this.authService.showModal('signup');
    }
    this.authService.authModalSubject$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ visible, activeScreen }) => {
        this.showModal = visible;
        if (activeScreen === 'login') {
          this.login();
        } else if (activeScreen === 'signup') {
          this.signup();
        } else if (this.screens.includes(activeScreen)) {
          this.activeScreen = activeScreen;
        } else {
          this.activeScreen = 'landing';
        }
      });

    this.checkQueryParams();
  }

  public checkQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroyed$))
      .subscribe((params) => {
        if (params['reverify'] === 'true') {
          this.showAuthModal(AUTH_SCREENS['SignUp']);
        }
      });
  }

  public showAuthModal(activeScreen: string): void {
    if (
      activeScreen === 'bankAuthenticationFailed' &&
      environment.razorAnalyticsFlag
    ) {
      this.analyticsService.stepEvent({
        eventName: 'bank_authentication_failed_screen',
      });
    }
    this.showModal = true;
    this.authService.showModal(activeScreen);
  }
  public closeAuthModal(): void {
    this.authService.hideModal();
  }

  public closeAuthSection(): void {
    this.showAuthSection = false;
  }

  private handleUserLogin(currentURL: URL, cardIdInRoute?: string): void {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    this.authForm?.patchValue({
      emailId: this.currentUser?.email,
    });

    const cardTokenMap: Record<string, string> =
      this.authService.getFromLocalStorage('cardTokenMap') ?? {};
    const paramStep = currentURL.searchParams.get('step');
    if (cardIdInRoute) {
      this.authService.saveInLocalStorage('userCardId', cardIdInRoute);
    }
    if (cardIdInRoute && cardIdInRoute in cardTokenMap) {
      if (paramStep === 'verification') {
        const transactionId = cardTokenMap[cardIdInRoute];
        this.authService.verifyTransaction(transactionId).subscribe({
          next: (res) => {
            /**
             * This means the user might have cancelled the flow or entered incorrect OTP details
             * This can also happen in case the flow is timed out or interrupted
             * In this case, we will redirect user to the card screen
             */
            if (res?.status !== 'success') {
              this.showAuthModal('bankAuthenticationFailed');
            }

            if (res?.authenticated && res?.card_id && res?.token) {
              const cardTokenMap = this.getCardTokenMap();
              cardTokenMap[res.card_id] = res.token;
              this.authService.saveInLocalStorage('cardTokenMap', cardTokenMap);
              this.authService.saveInLocalStorage('is3dsVerified', 'true');
              this.authService.saveInLocalStorage('isSignUp', 'false');
              const current_user =
                this.authService.getFromLocalStorage<User>('currentUser');
              if (current_user && current_user?.token) {
                current_user.token = res.jwt_token;
                this.authService.saveInLocalStorage(
                  'currentUser',
                  current_user
                );
              }
              this.showAuthModal('bankAuthenticated');
              this.setActiveScreen('bankAuthenticated');
            } else {
              this.showAuthModal('bankAuthenticationFailed');
            }
          },
          error: (err) => {
            this.showAuthModal('bankAuthenticationFailed');
            console.error('error occurred ', err);
          },
        });
      } else {
        this.showAuthModal('bankAuthenticationFailed');
      }
    } else {
      this.showAuthModal('bankAuthenticationFailed');
    }
  }

  private getCardTokenMap(): Record<string, string> {
    return (
      this.authService.getFromLocalStorage<Record<string, string>>(
        'cardTokenMap'
      ) ?? {}
    );
  }

  public ngDoCheck(): void {
    this.isLoggedIn = this.authService.isAuthenticated();
  }

  private checkMinLength(minLength: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control?.value;
      return value?.toString()?.length < minLength ? { minLength: true } : null;
    };
  }

  private checkMaxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const value = control?.value;
      return value?.toString()?.length > maxLength ? { maxLength: true } : null;
    };
  }

  private toFormGroup(elements: string[]): UntypedFormGroup {
    const group: { [key: string]: UntypedFormControl } = {};
    elements.forEach((key) => {
      group[key] = new UntypedFormControl('', Validators.required);
    });
    return new UntypedFormGroup(group);
  }

  private checkValidMMExpiry(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      let validMMDate = control?.value?.toString()?.length ? false : true;
      const monthInput = control?.value?.toString()
        ? parseInt(control?.value?.toString())
        : 0;
      if (monthInput >= 1 && monthInput <= 12) {
        validMMDate = true;
      }
      return validMMDate ? null : { invalidDate: true };
    };
  }
  private checkValidYYExpiry(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      let validYYDate = control?.value?.toString()?.length ? false : true;
      const yearInput = control?.value?.toString()
        ? parseInt(control?.value?.toString())
        : 0;
      const currentYear = parseInt(
        new Date().getFullYear().toString().substring(2, 4)
      );
      if (yearInput && yearInput >= currentYear && yearInput <= 99) {
        validYYDate = true;
      }
      return validYYDate ? null : { invalidDate: true };
    };
  }

  public submitCardDetails(): void {
    this.submitted = true;
    if (this.cardDetailsForm?.valid) {
      this.submitBtnDisable = true;
      this.authService.cardDetails = {
        cardNumber: this.cardNumber,
        expiryMonth: this.cardDetailsForm?.value?.expiryMM,
        expiryYear: this.cardDetailsForm?.value?.expiryYY,
      };
      this.authService.initiate3dsPayment()?.subscribe(
        (res) => {
          /**
           * This flow should be run when card is not 3ds verified
           * In that case the authenticated flag would be false
           * We redirect the user to the OTP flow by using url sent in response
           */
          if (
            res?.data?.formatted?.next?.redirect?.content &&
            res?.token &&
            res?.card_id &&
            !res?.authenticated
          ) {
            this.setActiveScreen('bankVerification');
            const cardTokenMap: Record<string, string> =
              this.authService.getFromLocalStorage('cardTokenMap') ?? {};
            cardTokenMap[res.card_id] = res.token;

            this.authService.saveInLocalStorage('cardTokenMap', cardTokenMap);

            const data = res.data.formatted.next.redirect;
            const {
              content: params,
              method = 'post',
              url: path,
              target = '',
              doc = window.document,
            } = data;

            const form = doc.createElement('form');
            form.method = method;
            form.action = path;
            if (target) {
              form.target = target;
            }

            for (const key in params) {
              if (Object.prototype.hasOwnProperty.call(params, key)) {
                const hiddenField = doc.createElement('input');
                hiddenField.type = 'hidden';
                hiddenField.name = key;
                hiddenField.value = params[key];

                form.appendChild(hiddenField);
              }
            }
            doc.body.appendChild(form);
            form.submit();
          } else if (res?.authenticated && res?.token && res?.card_id) {
            /**
             * In this case, the card is already 3ds verified, so no need to go through the OTP flow
             */
            const cardTokenMap: Record<string, string> =
              this.authService.getFromLocalStorage('cardTokenMap') ?? {};
            cardTokenMap[res.card_id] = res.token;
            this.authService.saveInLocalStorage('is3dsVerified', 'true');
            this.authService.saveInLocalStorage('isSignUp', 'false');
            this.authService.saveInLocalStorage('cardTokenMap', cardTokenMap);
            this.refreshPage();
          } else if (
            !res?.authenticated &&
            res?.card_id &&
            res?.data?.formatted?.status === 'N'
          ) {
            this.submitBtnDisable = true;
            this.customErrorMessage =
              'Please register again using a valid card with international payments enabled or check with you bank for more details';
            this.cardDetailsForm.setErrors({
              customError: true,
            });
          } else {
            this.submitBtnDisable = true;
          }
        },
        (err: {
          error: {
            errors: string[];
          };
        }) => {
          if (err?.error?.errors?.length) {
            this.customErrorMessage =
              this.env.staticTextContents.loginModal.errorMsgs.invalidCard;
            this.cardDetailsForm.setErrors({
              customError: true,
            });
          }
          this.submitBtnDisable = false;
          this.submitBtnTitle =
            this.env.staticTextContents.loginModal.submitBtnTitles.proceed;
          this.submitBtnName = 'submit-card-details-button';
        }
      );
    }
  }

  public setActiveElement(element: string | null): void {
    setTimeout(() => {
      this.focusedElement = element;
    }, 0);
  }

  public numericKeyDownEvent(event: KeyboardEvent, isMobile = false): void {
    if (
      ['Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
        event.key
      )
    ) {
      return;
    }

    if (!isMobile) {
      if (event.key === 'v' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        return;
      }
    }

    const isDigit = /\d/.test(event.key);

    if (
      !isDigit ||
      (isMobile && parseInt((event.target as HTMLInputElement).value) === 0)
    ) {
      event.preventDefault();
      return;
    }
  }

  public emailKeyDownEvent(event: KeyboardEvent): void {
    if (
      ['Tab', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(
        event.key
      )
    ) {
      return;
    }

    const isEmail = /^[a-z0-9._@%+-]/.test(event.key);
    if (!isEmail) {
      event.preventDefault();
      return;
    }
    return;
  }

  public cardKeyUpEvent(event: KeyboardEvent, index: number): void {
    let pos = index;
    if (
      event.key === 'Backspace' &&
      this.cardFormRows.toArray()[index]?.nativeElement?.value?.length == 0
    ) {
      pos = index - 1;
      if (pos > -1) {
        this.cardFormRows.toArray()[pos]?.nativeElement?.focus();
      }
    } else {
      if (
        this.cardFormRows.toArray()[index]?.nativeElement?.value?.length === 4
      ) {
        pos = index + 1;
      } else {
        return;
      }
    }
    if (pos > -1 && pos < 4) {
      this.cardFormRows.toArray()[pos]?.nativeElement?.focus();
    } else if (pos == 4) {
      if (this.authMode === 'email') {
        this.emailInputRef?.first?.nativeElement?.focus();
      } else {
        this.mobileInputRef?.first?.nativeElement?.focus();
      }
    }
  }
  public expiryMMKeyDownEvent(
    e: KeyboardEvent,
    nextHTMLElement: HTMLElement
  ): void {
    if (this.keyDownEvent(e)) {
      setTimeout(() => {
        if (Number?.isInteger(parseInt(e?.key))) {
          const monthInput = this.cardDetailsForm
            ?.get('expiryMM')
            ?.value?.toString();
          if (monthInput?.length === 2) {
            nextHTMLElement?.focus();
          }
        }
      }, 0);
    } else {
      e?.preventDefault();
    }
  }

  private keyDownEvent(e: KeyboardEvent): boolean {
    if (
      Number.isInteger(parseInt(e.key)) ||
      e.key === '/' ||
      e.key === 'Backspace' ||
      e.key === 'ArrowRight' ||
      e.key === 'ArrowLeft'
    ) {
      return true;
    } else {
      return false;
    }
  }

  public setActiveScreen(screen: string): void {
    this.showAuthSection = true;
    if (screen !== 'previous') {
      this.previousScreen = this.activeScreen;
      this.activeScreen = screen;
      this.submitted = false;
    }
    switch (screen) {
      case 'previous':
        this.setActiveScreen(this.previousScreen || 'mobile');
        break;
      case 'landing':
        this.submitBtnDisable = false;
        this.submitBtnTitle =
          this.env.staticTextContents.commonTexts.getStarted;
        this.submitBtnName = 'landing-get-started-button';
        break;
      case 'mobile':
        if (this.isSignUp) {
          this.submitBtnTitle =
            this.env.staticTextContents.loginModal.registerButtonLabel;
          this.submitBtnName = 'mobile-signup-button';
          if (environment.razorAnalyticsFlag) {
            this.analyticsService.milestoneEvent({
              eventName: 'signup_start',
              attributes: {
                mode: this.authMode === 'email' ? 'email' : 'mobile',
              },
            });
          }
        } else {
          this.submitBtnTitle =
            this.env.staticTextContents.loginModal.otpMessages.request;
          this.submitBtnName = 'mobile-login-button';
          if (environment.razorAnalyticsFlag) {
            this.analyticsService.milestoneEvent({
              eventName: 'login_start',
              attributes: {
                mode: this.authMode === 'email' ? 'email' : 'mobile',
              },
            });
          }
        }
        this.submitBtnDisable = !this.authForm?.valid;
        if (!this.changeNumberFLag) {
          this.authForm?.reset();
          this.otpForm?.reset();
          this.cardDetailsForm?.reset();
        }
        this.customErrorMessage = '';
        this.otpTimer = null;
        this.isCaptchaVerified = false;
        this.captchaToken = '';
        break;
      case 'otp':
        this.submitBtnDisable = true;
        this.submitBtnTitle =
          this.env.staticTextContents.loginModal.submitBtnTitles.submit;
        this.submitBtnName = 'otp-submit-button';
        this.initiateOtpTimer();
        setTimeout(() => {
          this.otpFormRows.toArray()[0]?.nativeElement?.focus();
        }, 0);
        if (environment.razorAnalyticsFlag) {
          this.analyticsService.stepEvent({
            eventName: this.isSignUp ? 'signup_otp_screen' : 'login_otp_screen',
          });
        }
        break;
      case 'card':
        this.addNewCard = true;
        this.submitBtnTitle =
          this.env.staticTextContents.loginModal.submitBtnTitles.authCard;
        this.submitBtnDisable = true;
        this.submitBtnName = 'card-authenticate-button';
        if (environment.razorAnalyticsFlag) {
          this.analyticsService.stepEvent({
            eventName: 'card_details_screen',
          });
        }
        break;
      case 'bankAuthenticated':
        if (localStorage.getItem('currentUser')) {
          this.isLoggedIn = true;
          setTimeout(() => {
            this.refreshPage();
          }, 2000);
          if (environment.razorAnalyticsFlag) {
            this.analyticsService.stepEvent({
              eventName: 'bank_authentication_screen',
            });
          }
        }
        break;
      default:
        if (environment.razorAnalyticsFlag) {
          this.analyticsService.stepEvent({
            eventName: screen,
          });
        }
        break;
    }
  }

  private initiateOtpTimer(seconds = 180): void {
    const minutes: number = Math.floor(seconds / 60);
    this.otpTimer =
      minutes +
      ':' +
      (seconds - minutes * 60 < 10
        ? `0${seconds - minutes * 60}`
        : seconds - minutes * 60);
    seconds--;
    const interval = setInterval(() => {
      if (this.otpTimer) {
        const minutes: number = Math.floor(seconds / 60);
        this.otpTimer =
          minutes +
          ':' +
          (seconds - minutes * 60 < 10
            ? `0${seconds - minutes * 60}`
            : seconds - minutes * 60);
        if (seconds > 0) {
          seconds--;
        } else {
          this.otpTimer = null;
          clearInterval(interval);
        }
      } else {
        clearInterval(interval);
      }
    }, 1000);
  }

  public handleSubmit(): void {
    if (this.activeScreen === 'landing') {
      if (
        (JSON.parse(localStorage.getItem('currentUser') || '{}') as User)?.token
      ) {
        const currentURL = new URL(window.location.href);
        this.handleUserLogin(currentURL);
        return;
      }
      this.setActiveScreen('mobile');
    } else if (this.activeScreen === 'mobile') {
      this.submitEmailId();
    } else if (this.activeScreen === 'otp') {
      this.submitOtp();
    } else if (this.activeScreen === 'card') {
      this.submitCardDetails();
    } else if (this.activeScreen === 'bankVerification') {
      this.setActiveScreen('bankAuthenticated');
    } else if (this.activeScreen === 'bankAuthenticated') {
      this.setActiveScreen('bankAuthenticated');
    }
  }

  public login(formReset = true): void {
    this.clearSession();
    this.isSignUp = false;
    this.handleCheckboxVisibility();
    this.authService.saveInLocalStorage('isSignUp', 'false');
    this.submitBtnTitle =
      this.env.staticTextContents.loginModal.otpMessages.request;
    this.submitBtnName = 'login-request-otp-button';
    this.submitted = false;

    if (formReset) {
      this.submitted = false;
      this.authForm.reset();
      this.authForm.markAsPristine();
      this.authForm.markAsUntouched();
      if (this.authMode === 'email') {
        this.authForm?.controls?.['emailId']?.reset();
        this.authForm?.controls?.['emailId']?.setErrors(null);
        this.authForm?.controls?.['emailId']?.updateValueAndValidity();
        this.emailId = '';
      } else {
        this.authForm?.controls?.['mobileNumber']?.reset();
        this.authForm?.controls?.['mobileNumber']?.setErrors(null);
        this.authForm?.controls?.['mobileNumber']?.updateValueAndValidity();
        this.mobileNumber = '';
      }
      if (this.authForm?.errors) {
        this.authForm.errors['customError'] = false;
      }
      this.customErrorMessage = '';
    }

    this.authForm?.controls?.['inpOne'].clearValidators();
    this.authForm?.controls?.['inpTwo'].clearValidators();
    this.authForm?.controls?.['inpThree'].clearValidators();
    this.authForm?.controls?.['inpFour'].clearValidators();

    this.authForm?.controls?.['inpOne'].updateValueAndValidity();
    this.authForm?.controls?.['inpTwo'].updateValueAndValidity();
    this.authForm?.controls?.['inpThree'].updateValueAndValidity();
    this.authForm?.controls?.['inpFour'].updateValueAndValidity();
    if (this.changeNumberFLag) {
      this.setActiveScreen('mobile');
    } else {
      this.handleSubmit();
    }
  }
  public signup(formReset = true): void {
    this.clearSession();
    this.isSignUp = true;
    this.handleCheckboxVisibility();
    this.authService.saveInLocalStorage('isSignUp', 'true');
    this.submitted = false;
    this.submitBtnTitle =
      this.env.staticTextContents.loginModal.registerButtonLabel;
    this.submitBtnName = 'signup-button';

    if (formReset && this.authForm) {
      this.authForm.reset();
      this.authForm.markAsPristine();
      this.authForm.markAsUntouched();
      if (this.authMode === 'email') {
        this.authForm?.controls?.['emailId']?.reset();
        this.authForm?.controls?.['emailId']?.setErrors(null);
        this.authForm?.controls?.['emailId']?.updateValueAndValidity();
        this.emailId = '';
      } else {
        this.authForm?.controls?.['mobileNumber']?.reset();
        this.authForm?.controls?.['mobileNumber']?.setErrors(null);
        this.authForm?.controls?.['mobileNumber']?.updateValueAndValidity();
        this.mobileNumber = '';
      }
      if (this.authForm?.errors) {
        this.authForm.errors['customError'] = false;
      }
      this.customErrorMessage = '';
    }
    this.authForm?.controls?.['inpOne'].setValidators([
      Validators.required,
      this.checkMinLength(4),
    ]);
    this.authForm?.controls?.['inpTwo'].setValidators([
      Validators.required,
      this.checkMinLength(4),
    ]);
    this.authForm?.controls?.['inpThree'].setValidators([
      Validators.required,
      this.checkMinLength(4),
    ]);
    this.authForm?.controls?.['inpFour'].setValidators([
      Validators.required,
      this.checkMinLength(4),
    ]);

    this.authForm?.controls?.['inpOne'].updateValueAndValidity();
    this.authForm?.controls?.['inpTwo'].updateValueAndValidity();
    this.authForm?.controls?.['inpThree'].updateValueAndValidity();
    this.authForm?.controls?.['inpFour'].updateValueAndValidity();
    if (this.changeNumberFLag) {
      this.setActiveScreen('mobile');
    } else {
      this.handleSubmit();
    }
  }

  public changeNumber(): void {
    this.changeNumberFLag = true;
    if (this.isSignUp) {
      this.signup(false);
    } else {
      this.login(false);
    }
  }

  public submitEmailId(isResend = false): void {
    this.submitted = true;
    if (this.authForm.valid && !this.submitBtnDisable) {
      this.submitBtnTitle =
        this.env.staticTextContents.loginModal.submitBtnTitles.requesting;
      this.submitBtnDisable = true;
      this.cardNumber = '';

      if (this.authMode === 'email') {
        this.emailId = this.authForm?.value?.emailId;
      } else {
        this.mobileNumber = this.authForm?.value?.mobileNumber;
      }

      if (this.isSignUp) {
        this.cardFormInput.forEach((inpNumber) => {
          this.cardNumber += this.authForm?.value[inpNumber]?.toString();
        });
        this.authService.cardDetails = {
          cardNumber: this.cardNumber,
          expiryMonth: '',
          expiryYear: '',
        };
        if (environment.razorAnalyticsFlag) {
          this.analyticsService.stepEvent({
            eventName: 'signup_screen_submit',
          });
        }
      }
      this.generateOtp(isResend);
    }
  }
  public submitOtp(): void {
    if (this.otpForm?.valid) {
      if (this.currentUser && this.currentUser?.id) {
        if (environment.recaptchaFlag && environment.production) {
          this.recaptchaV3Service
            .execute('otpSubmit')
            .subscribe((token: string) => {
              this.verifyOtp(token);
            });
        } else {
          this.verifyOtp();
        }
      } else {
        this.setActiveScreen('mobile');
      }
    }
  }
  private verifyOtp(token: string = ''): void {
    let code = '';
    this.submitted = true;
    this.submitBtnDisable = true;
    Object.values(this.otpForm?.value)?.map((val) => (code += val));
    this.authService
      .verifyOtp(
        this.userId,
        {
          code,
        },
        token
      )
      ?.subscribe(
        (res: {
          success?: boolean;
          token?: string;
          user_unlock_at?: Date;
          wrong_attempts_left?: number;
          error_message?: string;
        }) => {
          setTimeout(() => {
            this.submitBtnDisable = false;
          }, 500);
          if (res?.success && this.currentUser && res.token) {
            this.currentUser = {
              ...this.currentUser,
              token: res.token,
            };
            localStorage.setItem(
              'currentUser',
              JSON.stringify(this.currentUser)
            );
            // For Visa Hajj, also populate pvCurrentState with the token
            if (environment.populatePvCurrentState) {
              const currentState = JSON.parse(
                localStorage.getItem('pvCurrentState') ?? 'null'
              );
              localStorage.setItem(
                'pvCurrentState',
                JSON.stringify({
                  ...(currentState || {}),
                  token: res.token,
                })
              );
            }
            if (environment.razorAnalyticsFlag) {
              this.analyticsService.setIdentity(this.currentUser.id, {});
            }
            if (this.isSignUp) {
              this.setActiveScreen('card');
            } else {
              this.refreshPage();
            }
          } else {
            this.otpForm.setErrors({
              customError: true,
            });
            if (res?.user_unlock_at) {
              this.showOTPLimitError(res);
            }
            if (res.wrong_attempts_left && res.wrong_attempts_left > 0) {
              this.customErrorMessage =
                this.env.staticTextContents.loginModal.otpMessages.invalid +
                ' ' +
                this.env.staticTextContents.loginModal.otpMessages.attemptsLeft.replace(
                  '{{attempts}}',
                  res.wrong_attempts_left.toString()
                );
            }
            if (res.error_message === 'Verification failed') {
              this.customErrorMessage =
                this.env.staticTextContents.loginModal.errorMsgs.verificationFailed;
            }
          }
        },
        (err: {
          error: {
            errors: string[];
          };
        }) => {
          if (err?.error?.errors?.length) {
            this.customErrorMessage = err?.error?.errors[0];
            this.otpForm.setErrors({
              customError: true,
            });
          }
          this.submitBtnDisable = false;
        }
      );
  }

  private generateOtp(isResend = false): void {
    const observer = {
      next: (res: OtpResponse): void => {
        this.submitBtnDisable = false;
        if (res?.id || res?.user_id) {
          this.submitted = false;
          this.userId = res?.id || res?.user_id || '';
          this.currentUser = {
            ...this.currentUser,
            id: this.userId,
          };
          this.setActiveScreen('otp');
          isResend && this.resetOtp();
        } else if (res?.code === ERROR_CODES.CardAlreadyMappedWithUser) {
          this.setActiveScreen('mobile');
          this.customErrorMessage = ERROR_MESSAGES['CardAlreadyMappedWithUser'];
          this.authForm.setErrors({
            customError: true,
          });
        } else if (res?.code === ERROR_CODES.CardNotEligible) {
          this.setActiveScreen('mobile');
          this.customErrorMessage = ERROR_MESSAGES['CardNotEligible'];
          this.authForm.setErrors({
            customError: true,
          });
        } else if (res?.code === ERROR_CODES.InvalidUserCredentials) {
          this.setActiveScreen('mobile');
          this.customErrorMessage = ERROR_MESSAGES['InvalidUserCredentials'];
          this.authForm.setErrors({
            customError: true,
          });
        } else {
          this.customErrorMessage = this.env.useCustomSignupErrorMessage
            ? this.env.staticTextContents.loginModal.errorMsgs.defaultErrorMsg1
            : this.env.staticTextContents.loginModal.errorMsgs.defaultErrorMsg2;
          this.authForm.setErrors({
            customError: true,
          });
        }
      },
      error: (err: {
        error: {
          errors: string[];
          wrong_attempts_left: number;
          user_unlock_at: Date | string;
          next_otp_at: Date | string;
        };
      }): void => {
        this.setActiveScreen('mobile');
        if (err?.error?.errors?.length) {
          this.customErrorMessage = err?.error?.errors[0];
          if (err.error.user_unlock_at || err?.error?.next_otp_at) {
            const currentTime = new Date()?.getTime();
            const otpTime = new Date(
              err.error.user_unlock_at || err?.error?.next_otp_at
            )?.getTime();
            const diffTime = Math.ceil(
              Math.abs(otpTime - currentTime) / (1000 * 60)
            );
            this.customErrorMessage =
              this.customErrorMessage +
              ' ' +
              this.env.staticTextContents.loginModal.errorMsgs.tryAgain.replace(
                '{{time}}',
                diffTime.toString()
              );
          }
          this.authForm.setErrors({
            customError: true,
          });
        }
      },
    };

    // Build credentials based on authMode
    const credentials: { email?: string; mobile?: string } =
      this.authMode === 'email'
        ? { email: this.emailId }
        : { mobile: this.mobileNumber };

    this.authService
      .generateOtp(this.isSignUp, credentials)
      ?.subscribe(observer);
    this.otpForm?.reset();
    return;
  }

  private resetOtp(): void {
    if (environment.razorAnalyticsFlag) {
      this.analyticsService.stepEvent({
        eventName: 'otp_resent',
      });
    }
    this.isOtpResent = true;
    setTimeout(() => {
      this.isOtpResent = false;
    }, 3000);
  }
  private showOTPLimitError(res: VerifyOtpResponse): void {
    const currentTime = new Date()?.getTime();
    const lockDuration = res?.user_unlock_at
      ? new Date(res.user_unlock_at).getTime()
      : 0;
    if (lockDuration > currentTime) {
      const diffTime = Math.ceil(
        Math.abs(lockDuration - currentTime) / (1000 * 60)
      );
      this.customErrorMessage =
        this.env.staticTextContents.loginModal.otpMessages.maxInvalid;
      this.customErrorMessage =
        this.customErrorMessage +
        ' ' +
        this.env.staticTextContents.loginModal.errorMsgs.tryAgain.replace(
          '{{time}}',
          diffTime.toString()
        );
      if (environment.razorAnalyticsFlag) {
        this.analyticsService.stepEvent({
          eventName: 'otp_limit_reached',
        });
      }
      return;
    }
  }
  public clearMobileInput(): void {
    this.authForm?.reset();
  }

  public resendOTP(): void {
    if (!this.otpTimer) {
      if (this.authMode === 'email') {
        this.emailId = this.authForm?.value?.emailId;
      } else {
        this.mobileNumber = this.authForm?.value?.mobileNumber;
      }
      if (this.userId) {
        this.generateOtp(true);
      } else {
        this.setActiveScreen('mobile');
      }
    }
  }

  public focusFirstEmptyInput(): void {
    const firstEmptyBoxIndex = this.otpFormRows.toArray()?.findIndex((row) => {
      return Number.isNaN(parseInt(row?.nativeElement?.value));
    });
    this.otpFormRows.toArray()[firstEmptyBoxIndex]?.nativeElement?.focus();
  }
  public otpKeyUpEvent(event: KeyboardEvent, index: number): void {
    let pos = index;
    if (event.keyCode === 8 && event.which === 8) {
      pos = index - 1;
      if (pos > -1) {
        this.otpForm.controls[this.otpFormInput[pos]].setValue(null);
      }
    } else {
      if (this.otpForm?.controls[this.otpFormInput[pos]]?.value?.toString()) {
        pos = index + 1;
      } else {
        return;
      }
    }
    if (pos > -1 && pos < this.otpFormInput.length) {
      this.otpFormRows.toArray()[pos].nativeElement.focus();
    }
  }

  public onOtpPaste(e: ClipboardEvent): void {
    e.preventDefault();
    return; // COMMENT - Preventing paste event for now as there are bugs in the current implementation
    const pasteData = e?.clipboardData?.getData('text') || '';
    if (pasteData?.length === 6) {
      if (Number.isInteger(parseInt(pasteData))) {
        this.otpFormInput.forEach((val, index) => {
          this.otpForm.controls[val].setValue(parseInt(pasteData[index]));
        });
        this.otpFormRows
          .toArray()
          [this.otpFormInput?.length - 1].nativeElement.focus();
      }
    }
  }

  public tryAgain(): void {
    this.analyticsService.removeIdentity();
    this.authService
      .deleteToken()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clearSessionAndNavigate();
          return;
        },
        error: () => {
          this.clearSessionAndNavigate();
          return;
        },
      });
    if (environment.razorAnalyticsFlag) {
      this.analyticsService.stepEvent({
        eventName: 'reverify_start',
      });
    }
    return;
  }
  private clearSession(): void {
    this.storageService.clearLocalStorage();
    return;
  }
  private clearSessionAndNavigate(): void {
    this.clearSession();
    this.router.navigate(['/']);
    this.setActiveScreen('mobile');
    return;
  }

  public refreshPage(): void {
    this.router.navigate(['/']);
    this.authService.hideModal();
  }

  public onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length > input.maxLength) {
      input.value = input.value.slice(0, input.maxLength);
    }
  }

  public handleCheckboxVisibility(): void {
    this.showCheckboxes =
      this.isSignUp || this.env.loginModal.showLoginCheckboxes;
    if (this.showCheckboxes) {
      this.authForm.controls['consent1'].enable();
      this.authForm.controls['consent2'].enable();
    } else {
      this.authForm.controls['consent1'].disable();
      this.authForm.controls['consent2'].disable();
    }
  }
}
